/**
 * StoryView (web) — a story's whole article as travelers read it: the cover header (title + description),
 * then the blocks on the left with the cover credit and author at the foot, and a sticky section nav on
 * the right (desktop).
 *
 * `StoryPage` renders it for a published story, and the admin's story editor renders it as its preview
 * (handed through `AdminContext.renderStoryPreview` by `AdminEmbed`), so the preview is this exact
 * component and can't drift from the real page. A draft can be incomplete, hence the optional cover.
 */
import type { Story, StoryImageAsset } from '@elara/client-core/stories/stories';

import { STORY_AUTHORS } from '@elara/client-core/stories/stories';

import { AppImage, Avatar, AvatarFallback, AvatarImage } from '#ui';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import { storyImageProps } from '#views/stories/story-assets.js';
import { StoryArticle } from '#views/stories/StoryArticle.js';
import { StoryNav } from '#views/stories/StoryNav.js';

/** What the view needs: a published story, or a draft that may not have its cover yet. */
export type StoryViewContent = Pick<Story, 'title' | 'description' | 'author' | 'coverCredit' | 'content'> & {
  coverImage?: StoryImageAsset | null;
};

/** The header cover spans the page column: up to 1024px (`max-w-5xl`), the full viewport below that. */
const COVER_SIZES = '(min-width: 1024px) 1024px, 100vw';

export function StoryView({ story }: { story: StoryViewContent }) {
  const author = STORY_AUTHORS[story.author];

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 pt-4 pb-16 md:pt-8">
      {/* Cover header — title + description over a progressive blur + scrim. The image fills the
          header and a single grid cell stacks an aspect-ratio spacer under the text, so 16/7 acts as
          a minimum height that a long title/description grows past instead of overflowing. */}
      <header className="relative grid overflow-hidden rounded-2xl bg-background-tertiary">
        {story.coverImage && (
          <AppImage
            {...storyImageProps(story.coverImage, COVER_SIZES)}
            alt={story.title}
            loading="eager"
            className="absolute inset-0 size-full object-cover"
          />
        )}
        <div className="col-start-1 row-start-1 aspect-[16/7] w-full" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 [backdrop-filter:blur(24px)] [-webkit-backdrop-filter:blur(24px)] [mask-image:linear-gradient(to_bottom,transparent,black)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black)]" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="relative col-start-1 row-start-1 flex flex-col justify-end gap-2 p-6 text-white">
          <h1 className="max-w-2xl font-display text-3xl text-balance font-light md:text-4xl [text-shadow:0_1px_3px_rgb(0_0_0_/_0.35)]">
            {story.title}
          </h1>
          <p className="max-w-2xl text-white/90 [text-shadow:0_1px_3px_rgb(0_0_0_/_0.35)] text-balance">
            {story.description}
          </p>
        </div>
      </header>

      {/* Article + sticky nav */}
      <div className="flex gap-10">
        <div className="flex min-w-0 flex-1 flex-col gap-8">
          <StoryArticle story={story} />

          {/* Cover attribution — a licensed cover is only licensed to us while its credit ships. */}
          {story.coverCredit && (
            <SimpleMarkdown text={story.coverCredit} className="text-xs text-muted-foreground [&_a]:underline" />
          )}

          {/* Author, at the foot of the article. */}
          <div className="flex flex-col gap-3 border-t border-border pt-6">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Curated by</div>
            <div className="flex items-center gap-3">
              <Avatar className="size-10">
                <AvatarImage src={`/team/${author.id}.jpg`} alt={author.name} />
                <AvatarFallback>{author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <div className="text-sm font-medium">{author.name}</div>
                <div className="text-xs text-muted-foreground">{author.role}</div>
              </div>
            </div>
          </div>
        </div>
        <aside className="hidden w-64 shrink-0 lg:block">
          <StoryNav story={story} />
        </aside>
      </div>
    </div>
  );
}
